import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { Router } from "@angular/router";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { UserService } from "@main/users";
import { IEpisode } from "@project/models/episodes/episode";
import { FormIODefinition } from "@features/bb-formio";
import { FormIODefinitionService } from "@features/bb-formio/services/formio-definition.service";
import {
    MultiUserFormAssociation,
    NewMultiUserFormAssociation
} from "@main/formio/multi-user-form/dto/multi-user-form-associations.dto";
import { MultiUserFormService } from "@main/formio/multi-user-form/services/multi-user-form.service";
import { MultiUserFormDef } from "@main/formio/multi-user-form/dto/multi-user-form.dto";
import { FormIODataService } from "@features/bb-formio/services/formio-data.service";
import {
    MultiUserFormAssociationsService
} from "@main/formio/multi-user-form/services/multi-user-form-associations.service";
import { FormEventType } from "./event-type";
import { FormEventsDTO } from "@main/formio/dto/formEventsDTO";
import { FormEventsService } from "@main/formio/services/formEvents.service";
import { UserHelperService } from "@main/users/user-helper.service";
import { EpisodeService } from "@project/episode/episode.service";
import { FormioViewerService } from "@main/formio/services/formioViewer.service";
import { Subscription } from "rxjs";
import { StageTargetType } from "../../multi-user-form/dto/multi-user-form-stage.dto";
import { FormEventPayload } from "../../dto/formEventsDTO";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { CustomComponentTypes } from "../data-insertion-dialog/customComponentTypes";
import { AppStorage } from "@bbwt/utils/app-storage";
import { OrganizationService } from "@main/companies";
import { SelfRegistrationFormService } from "@project/self-registration/forms/self-registration-form.service";


@Component({
    selector: "form-event-workflow",
    templateUrl: "./form-event-workflow.component.html",
    styleUrls: ["./form-events.component.scss"]
})
export class FormEventWorkflowComponent implements OnInit, OnDestroy {

    currentFormDef: FormIODefinition = null;
    currentMUFDef: MultiUserFormDef = null;

    displaySelectEpisodeDialog: boolean = false;
    displayMufCreateInstanceDialog: boolean = false;
    displayNewFormRequestDialog: boolean = false;
    displaySelectPatientDialog: boolean = false;


    episodeRequired: boolean = false;
    restoreSelectControlState = false;
    restoreDuplicateCheakState = false;
    restoreSelectedEpisode: IEpisode = null;
    mufAssoc: MultiUserFormAssociation = null;
    newMufAssocData: NewMultiUserFormAssociation = null;


    @Output() readonly onCancel = new EventEmitter();
    @Output() readonly onSelect = new EventEmitter<FormIODefinition>();

    currentEpisode: IEpisode = null;
    @Input() formEventType: FormEventType;
    @Input() returnUrl: string;
    @Input() backNavigationParams: { [key: string]: string | number | boolean } = {};
    @Input() formEvent: FormEventsDTO = null;

    isReadOnlyEpisode: boolean = false;
    currentformEvent: FormEventsDTO = null;
    isMandatoryForm = false;
    currentPatientId: string;
    formEventLoaded: boolean = false;
    currentWalkInId: string = null;

    private subscription: Subscription;
    payload: FormEventPayload = null;
    currentUrl: string = null;

    anonymousSelfRegForm: boolean;
    selfRegOrgUrl: string;
    selfRegPatientId: string;

    constructor(
        private selfRegistrationFormService: SelfRegistrationFormService,
        private formIODefinitionService: FormIODefinitionService,
        private patientService: PatientService,
        private userService: UserService,
        private episodeService: EpisodeService,
        private organizationService: OrganizationService,
        private multiUserFormService: MultiUserFormService,
        private router: Router,
        private formIODataService: FormIODataService,
        private multiUserFormAssociationService: MultiUserFormAssociationsService,
        private formioViewerService: FormioViewerService,
        private formEventsService: FormEventsService,
        public appOnlineStateService: AppOnlineStateService,
        public userHelperService: UserHelperService
    ) {
        this.subscription = this.formEventsService.event$.subscribe((payload: FormEventPayload) => {
            if (payload.formEventType === this.formEventType) {
                this.handleFromEvent(payload);
            }
        });
    }

    get isAppOnline() {
        return this.appOnlineStateService.isAppOnline;
    }

    async ngOnInit() {
        this.currentPatientId = this.patientService.currentPatientId;
        this.formEventLoaded = false;

        this.currentformEvent = this.formEvent ? this.formEvent : await this.formEventsService.getCurrentFormEvent(this.formEventType);

        if (!this.currentformEvent) {
            return;
        }

        this.isMandatoryForm = this.currentformEvent.isMandatoryForm;
        this.isReadOnlyEpisode = true;

        if (this.currentformEvent.eventType !== FormEventType.SelfRegisterPatient || this.userService.isLogged){
            this.currentFormDef = await this.formIODefinitionService.get(this.currentformEvent.formDefinitionId);
        }

        this.episodeRequired = this.currentFormDef?.isEpisodeLocked;
        this.currentUrl = this.router.url;
        this.formEventLoaded = true;
    }

    async handleFromEvent(payload: FormEventPayload): Promise<void> {
        if (!this.currentformEvent || !this.isAppOnline) {
            this.redirectToReturnUrl();
            return;
        }

        if (payload.formEventType === FormEventType.SelfRegisterPatient) {
            this.currentEpisode = payload.selfRegisterOrganizationUrl
                ? await this.selfRegistrationFormService.getEpisodeByUrl(payload.selfRegisterOrganizationUrl)
                : await this.selfRegistrationFormService.getEpisodeByOrgId(payload.selfRegisterOrganizationId);

            if (!this.userService.isLogged && payload.selfRegisterOrganizationUrl) {
                this.handleAnonSelfRegFormEvent(payload);
                return;
            }
        }

        if (this.currentEpisode){
            this.currentEpisode = await this.formIODefinitionService.ensureInstanceEpisodeExistsForPatient(this.currentEpisode, payload.patientId);
        }

        this.payload = payload;
        this.currentWalkInId = payload.walkInId;
        this.patientService.currentPatientId = this.currentPatientId = payload.patientId;

        if (payload.episodeId) {
            this.currentEpisode = await this.episodeService.get(payload.episodeId);
        }

        const missingPatient = !this.currentPatientId;
        const requestOnlyForm = this.currentFormDef?.byRequestOnly;
        const mufCapable = this.currentFormDef?.activeRevision?.mufCapable;

        switch (true) {
            case missingPatient:
                this.displaySelectPatientDialog = true;
                break;

            case requestOnlyForm:
                this.displayNewFormRequestDialog = true;
                break;

            case mufCapable:
                this.currentMUFDef = await this.multiUserFormService.getByFormRevisionId(this.currentFormDef.activeRevisionId);
                this.displayMufCreateInstanceDialog = true;
                break;

            default:
                this.navigateToFormDisplay();
                break;
        }
    }

    async handleAnonSelfRegFormEvent(payload: FormEventPayload): Promise<void> {
        const formDef = await this.selfRegistrationFormService.getFormDefinitionByOrgUrl(payload.selfRegisterOrganizationUrl);
        const mufCapable = formDef?.activeRevision?.mufCapable;

        this.anonymousSelfRegForm = true;
        this.selfRegOrgUrl = payload.selfRegisterOrganizationUrl;
        this.selfRegPatientId = payload.patientId;

        if (mufCapable) {
            this.currentMUFDef = await this.selfRegistrationFormService.getMufDefinitionByUrl(payload.selfRegisterOrganizationUrl);
            this.displayMufCreateInstanceDialog = true;
            return;
        }

        this.router.navigate(["selfregistration/add-form"], {
            queryParams: {
                patientId: payload.patientId,
                clinic: payload.selfRegisterOrganizationUrl
            }
        });
    }

    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    private async navigateToFormDisplay() {
        if (this.mufAssoc) {
            this.redirectToMUFInstance();
            this.mufAssoc = null;
            return;
        }

        const latestRevisionForCurrentForm = await this.formioViewerService.get(this.currentFormDef.id);

        let queryParams = {};

        queryParams["formId"] = this.currentFormDef.id;

        if (this.currentEpisode) {
            queryParams["episodeId"] = this.currentEpisode.id;
            queryParams["isReadOnlyEpisode"] = this.currentEpisode != null
        }

        queryParams["revisionId"] = latestRevisionForCurrentForm.activeRevision.id;

        queryParams["generatePdf"] = this.currentFormDef.activeRevision.shareOnComplete;
        queryParams["patientId"] = this.currentPatientId;
        queryParams["isMandatoryForm"] = this.isMandatoryForm;

        if (this.currentWalkInId) {
            queryParams["walkInId"] = this.currentWalkInId;
        }

        queryParams["returnUrl"] = this.returnUrl ? this.returnUrl : this.currentUrl;

        if (Object.keys(this.backNavigationParams).length > 0) {
            AppStorage.setItemForSession("backNavigationParams", this.backNavigationParams);
            queryParams["useBackNavigationParams"] = true;
        }

        const componentTypes: CustomComponentTypes = await this.formIODefinitionService.getCustomComponentTypes(this.currentFormDef.id)
        if (componentTypes &
            (CustomComponentTypes.ConsultationHistoryV2
                | CustomComponentTypes.ConsultationHistory
                | CustomComponentTypes.EpisodeNotes
                | CustomComponentTypes.MedicationHistory)) {
            await this.redirectToDataInsertion(queryParams);
            return;
        }

        this.router.navigate(["app/formio/display"], {queryParams});
    }

    onCancelMufInstanceDialog() {
        this.currentMUFDef = null;
        this.mufAssoc = null;
        this.displayMufCreateInstanceDialog = false;
        this.redirectToReturnUrl();
    }

    async onCreateMufInstanceDialog(mufAssocData: NewMultiUserFormAssociation) {
        this.newMufAssocData = mufAssocData;
        if (this.anonymousSelfRegForm) {
            await this.handleAnonSelfRegMufCreation(mufAssocData);
            return;
        }

        var selfTargetStage = this.newMufAssocData.multiUserFormAssociationLinks.some(x => x.userId == this.userService.currentUser.id)

        if (!this.currentEpisode && this.episodeRequired && !selfTargetStage) {
            this.displaySelectEpisodeDialog = true;
            return;
        }
        this.mufAssoc = await this.multiUserFormAssociationService.addNewMUFAssociation(this.newMufAssocData);
        this.displayMufCreateInstanceDialog = false;
        this.redirectToMUFInstance();
    }

    async onSkipOkEpisodeSelectDialog(episode: IEpisode = null) {
        this.currentEpisode = episode;
        this.displaySelectEpisodeDialog = false;

        if (this.newMufAssocData) {
            this.newMufAssocData.episodeId = this.currentEpisode.id;
            this.newMufAssocData.isReadOnlyEpisode = true;
            this.mufAssoc = await this.multiUserFormAssociationService.addNewMUFAssociation(this.newMufAssocData);
        }
        this.redirectToMUFInstance();
    }


    onCreateFormRequestDialog() {
        this.displayNewFormRequestDialog = false;
        this.redirectToReturnUrl()

    }

    redirectToReturnUrl() {
        if (this.returnUrl) {
            if (Object.keys(this.backNavigationParams).length > 0) {
                return this.router.navigate([this.returnUrl], {queryParams: this.backNavigationParams});
            } else {
                this.router.navigateByUrl(this.returnUrl);
            }
        }
    }

    onCancelFormRequestDialog() {
        this.displayNewFormRequestDialog = false;
        this.redirectToReturnUrl();
    }

    redirectToMUFInstance() {
        let startMufSelf = this.mufAssoc.multiUserFormAssociationLinks.find(x => x.multiUserFormStage.sequenceStepIndex == 1)?.userId == this.userService.currentUser.id;
        this.currentMUFDef = null;
        this.displayMufCreateInstanceDialog = false;

        if (!startMufSelf) {
            this.redirectToReturnUrl();
            return;
        }

        const queryParams = {
            mufId: this.mufAssoc.id,
            target: this.userService.currentUser.id,
            formDataId: this.mufAssoc.formDataId,
            isReadOnlyEpisode: this.currentEpisode != null,
        }
        queryParams["returnUrl"] = this.returnUrl ? this.returnUrl : this.currentUrl;

        if (Object.keys(this.backNavigationParams).length > 0) {
            AppStorage.setItemForSession("backNavigationParams", this.backNavigationParams);
            queryParams["useBackNavigationParams"] = true;
        }

        this.router.navigate(["app/formio/multiuser/display"], {queryParams});
        this.mufAssoc = null;
    }

    onOkSelectPatientDialog(patientId: string) {
        this.patientService.currentPatientId = patientId;
        this.payload.patientId = patientId;
        this.displaySelectPatientDialog = false;
        this.handleFromEvent(this.payload);
    }

    async redirectToDataInsertion(queryParams: { [key: string]: string | number; }) {
        await this.router.navigate(["app/formio/data-insertion"], {queryParams});
    }

    private async handleAnonSelfRegMufCreation(mufAssocData: NewMultiUserFormAssociation) {
        this.mufAssoc = await this.selfRegistrationFormService.addNewMufAssociationByUrl(this.selfRegOrgUrl, mufAssocData);
        this.displayMufCreateInstanceDialog = false;

        const firstStage = this.mufAssoc.multiUserFormDefinition.multiUserFormStages.shift();
        let startMufSelf = firstStage.stageTargetType === StageTargetType.Self;
        this.currentMUFDef = null;

        if (!startMufSelf) {
            this.redirectToReturnUrl();
            return;
        }

        this.router.navigate(["selfregistration/add-muf"], {
            queryParams: {
                patientId: this.selfRegPatientId,
                clinic: this.selfRegOrgUrl
            }
        });
        this.mufAssoc = null;
    }
}
